import React, { useEffect, useMemo } from 'react';

import { NewsTickerItemDataFull } from '@/models/feed';
import { Badge, Title } from '@/models/profile';
import { Reward } from '@/models/misc';
import { setLangIso } from '@/state/features/app';
import store, { useAppDispatch, useAppSelector } from '@/state/store';
import { DEFAULT_LANG } from '@/globals';

interface TranslatedTextProps {
    translationKey: string;
    fallback?: string;
}

function TranslatedText({ translationKey, fallback }: TranslatedTextProps) {
    // Implementation will be added later
    return <>{fallback || translationKey}</>;
}

// ⚠️ This must be kept up to date with UI_TRANSLATION_KEYS model in cms-v2 repo! ⚠️
type UITranslationKeys =
    // General
    | 'title'
    | 'share'
    | 'copy'
    | 'copied'
    | 'close'
    | 'loading'
    // Profile Drawer
    | 'profileDrawer_quest'
    | 'profileDrawer_mission'
    | 'profileDrawer_scorecard'
    | 'profileDrawer_titles'
    | 'profileDrawer_level'
    | 'profileDrawer_yourLevel'
    | 'profileDrawer_incomplete'
    | 'profileDrawer_complete'
    | 'profileDrawer_titlesUnlocked'
    | 'profileDrawer_yourStats'
    | 'profileDrawer_levels'
    | 'profileDrawer_shareError'
    | 'profileDrawer_titleNotFound'
    | 'profileDrawer_hint'
    | 'profileDrawer_logoImage'
    // Loading Screen
    | 'loading_message1Title'
    | 'loading_message1Subtitle'
    | 'loading_message2Title'
    | 'loading_message2Subtitle'
    | 'loading_message3Title'
    | 'loading_message3Subtitle'
    // Information Screen
    | 'informationScreen_introText'
    | 'informationScreen_mainText'
    | 'informationScreen_topForegroundImageAltText'
    | 'informationScreen_poweredBy'
    | 'informationScreen_shareText'
    | 'informationScreen_additionalFooterText'
    | 'informationScreen_topForegroundImage'
    // Legal
    | 'legal_privacyPolicy'
    | 'legal_privacyChoices'
    | 'legal_californiaNotice'
    | 'legal_filmRatings'
    | 'legal_termsOfUse'
    | 'legal_mpa'
    // Notifications
    | 'notification_badgeActionPrefix'
    | 'notification_titleActionPrefix'
    | 'notification_questCompleteActionPrefix'
    | 'notification_missionCompleteActionPrefix'
    // Rarities
    | 'rarity_common'
    | 'rarity_uncommon'
    | 'rarity_rare'
    | 'rarity_epic'
    | 'rarity_legendary'
    // My Rewards Drawer
    | 'myRewards_title'
    | 'myRewards_emptyText'
    // Reward Claim Errors
    | 'rewardClaim_errorTitle'
    | 'rewardClaim_errorNoCodesDescription'
    | 'rewardClaim_errorLoseDescription'
    | 'rewardClaim_errorCooldownDescription'
    | 'rewardClaim_errorInactiveDescription'
    | 'rewardClaim_errorGenericDescription'
    // Discord Auth
    | 'discord_authenticating'
    | 'discord_sdkLoading'
    | 'discord_authorizePrompt'
    | 'discord_authorizeButton';

interface TranslatedUITextProps {
    translationKey: UITranslationKeys;
    fallback?: string;
}

export function TranslatedUIText({ translationKey, fallback }: TranslatedUITextProps) {
    const translation = useUITranslatedText(translationKey, fallback);
    return <>{translation}</>;
}

export default TranslatedText;

// Core translation lookup logic
function getTranslation(
    translations: Record<string, Record<string, string>> | undefined,
    langIso: string | undefined,
    translationKey: string,
    fallback?: string,
): string {
    const translation = translations?.[langIso]?.[translationKey];

    if (translation) {
        return translation;
    }

    if (translations?.[langIso] && process.env.PUBLIC_SUPPORT_TRANSLATIONS === 'true') {
        console.warn(`Translation for key "${translationKey}" not found in language "${langIso}". Using fallback.`);
    }

    return fallback || '';
}

export function useTranslatedText(translationKey: string, fallback?: string) {
    const langIso = useAppSelector((state) => state.app.langIso);
    const translations = useAppSelector((state) => state.app.translations);
    return getTranslation(translations, langIso, translationKey, fallback);
}

export function useUITranslatedText(key: UITranslationKeys, fallback?: string) {
    const fullKey = `platform__ui__${key}`;
    return useTranslatedText(fullKey, fallback);
}

// Non-hook version for use outside React components (e.g. Redux thunks)
export function getUITranslatedText(key: string, fallback: string): string {
    const state = store.getState();
    const { langIso, translations } = state.app;
    const fullKey = `platform__ui__${key}`;
    return getTranslation(translations, langIso, fullKey, fallback);
}

export function useLang() {
    const current = useAppSelector((state) => state.app.langIso);
    const availableLangs = useAppSelector((state) => state.app.availableLangs) ?? [];

    // Put default lang to the beginning if it exists
    const langs = useMemo(
        () => [
            ...availableLangs.filter((l) => l.langISO?.toLowerCase() === DEFAULT_LANG.langISO),
            ...availableLangs.filter((l) => l.langISO?.toLowerCase() !== DEFAULT_LANG.langISO),
        ],
        [availableLangs],
    );

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (current) document.documentElement.lang = current;
    }, [current]);

    function setLang(langIso: string) {
        if (process.env.PUBLIC_SUPPORT_TRANSLATIONS !== 'true') {
            // setLang ignored as translations are disabled
            return;
        }
        dispatch(setLangIso(langIso));
    }

    return {
        current,
        langs,
        setLang,
    };
}

// Title translations
// Keys are in format: platform__title__{id}__{field}
// e.g. platform__title__chef__title, platform__title__chef__description
export function getTitleTranslatedText(titleId: string, field: string, fallback: string): string {
    const state = store.getState();
    const { langIso, translations } = state.app;
    const fullKey = `platform__title__${titleId}__${field}`;
    return getTranslation(translations, langIso, fullKey, fallback);
}

// Badge translations
// Keys are in format: platform__badge__{rank}__{field}
// e.g. platform__badge__gold__description, platform__badge__gold__name
export function getBadgeTranslatedText(rank: string, field: string, fallback: string): string {
    const state = store.getState();
    const { langIso, translations } = state.app;
    const fullKey = `platform__badge__${rank}__${field}`;
    return getTranslation(translations, langIso, fullKey, fallback);
}

// NewsTicker translations
// Keys are in format: platform__newsticker__{id}__{field}
// e.g. platform__newsticker__desk_hide__recentEventsMessageTemplate
export function getNewsTickerTranslatedText(id: string, field: string, fallback: string): string {
    const state = store.getState();
    const { langIso, translations } = state.app;
    const fullKey = `platform__newsticker__${id}__${field}`;
    return getTranslation(translations, langIso, fullKey, fallback);
}

export function getTranslatedBadge(badge: Badge): Badge {
    if (!badge) return;
    const rank = badge?.rank;
    if (!rank) {
        console.log(`Cannot translate badge as rank missing: ${badge}`);
        return badge;
    }
    return {
        ...badge,
        name: getBadgeTranslatedText(rank, 'name', badge.name),
        description: getBadgeTranslatedText(rank, 'description', badge.description),
    };
}

export function getTranslatedTitle(title: Title): Title {
    if (!title) return;
    const key = title?.id;
    if (!key) {
        console.log(`Cannot translate title as id missing: ${title}`);
        return title;
    }
    return {
        ...title,
        title: getTitleTranslatedText(key, 'title', title.title),
        description: getTitleTranslatedText(key, 'description', title.description),
        hint: getTitleTranslatedText(key, 'hint', title.hint || title.description),
    };
}

// Reward translations
// Keys are in format: platform__reward__{id}__{field}
// e.g. platform__reward__abc123__name, platform__reward__abc123__modalTitle
export function getRewardTranslatedText(id: string, field: string, fallback: string): string {
    const state = store.getState();
    const { langIso, translations } = state.app;
    const fullKey = `platform__reward__${id}__${field}`;
    return getTranslation(translations, langIso, fullKey, fallback);
}

export function getTranslatedReward(reward: Reward | null | undefined): Reward | null | undefined {
    if (!reward) return reward;
    const id = reward?.id;
    if (!id) {
        console.log(`Cannot translate reward as id missing: ${reward}`);
        return reward;
    }
    return {
        ...reward,
        name: getRewardTranslatedText(id, 'name', reward.name),
        modalTitle: getRewardTranslatedText(id, 'modalTitle', reward.modalTitle),
        description: getRewardTranslatedText(id, 'description', reward.description),
        mainImagePath: getRewardTranslatedText(id, 'mainImage', reward.mainImagePath),
        redemptionInstructions: getRewardTranslatedText(id, 'smallPrintText', reward.redemptionInstructions),
        readMoreUrl: getRewardTranslatedText(id, 'readMoreURL', reward.readMoreUrl),
        readMoreText: getRewardTranslatedText(id, 'readMoreText', reward.readMoreText),
        ctaText: getRewardTranslatedText(id, 'ctaText', reward.ctaText),
        externalUrl:
            reward.ctaMethod === 'open_link'
                ? getRewardTranslatedText(id, 'ctaOpenLinkURL', reward.externalUrl)
                : reward.externalUrl,
        messageText:
            reward.ctaMethod === 'send_dm'
                ? getRewardTranslatedText(id, 'discordDmMessage', reward.messageText)
                : reward.messageText,
    };
}

export function getTranslatedNewsTickerItem(
    item: NewsTickerItemDataFull,
    type: 'personal' | 'recent',
): NewsTickerItemDataFull {
    if (!item) return;
    const id = item?.id;
    if (!id) {
        console.log(`Cannot translate newsTickerItem as id missing: ${item}`);
        return item;
    }
    const field = type === 'recent' ? 'recentEventsMessageTemplate' : 'userMessageTemplate';
    return {
        ...item,
        message: getNewsTickerTranslatedText(id, field, item.message),
    };
}
